/**
 * Run one signed operation against `/api/me/encryption/*` with a nonce nothing
 * else has touched.
 *
 * The API issues one live challenge per member (two minutes) and consumes it on
 * the next signed call whether or not the signature verifies
 * (signed-operation.guard.ts). So a signed call is always three steps —
 * challenge, sign, call — and the one failure worth retrying is `stale-nonce`:
 * the nonce expired or an earlier attempt spent it, and a fresh challenge with a
 * fresh signature fixes it. A `signature-rejected` is not that: the key is
 * wrong, and retrying only burns another nonce, so it is rethrown untouched.
 *
 * This module imports no cryptography. The `signer` is supplied by the caller
 * — the crypto-primitives ticket delivers bundle-key signing and the caller
 * closes over it — and receives the nonce plus the operation context so it can
 * build the canonical message `{ op, memberId, nonce, ...fields }` itself.
 *
 * The signer must resolve to the base64 signature STRING. `courses-crypto`'s
 * `signOperation({ signingKey, op, memberId, nonce, fields })` resolves to
 * `{ message, signature }`, so a caller closing over it passes `.signature`:
 *
 *   nonce => (await signOperation({ signingKey, op: 'approve', memberId, nonce,
 *     fields: { deviceId, wrappedMkHash: await wrappedMkHash(wrappedMk) } })).signature
 *
 * Spec: docs/plans/2026-09-02-next-steps-e2e-encryption.md section 6.1
 * ("Signed operations"); contract section 5.
 */
import { isEncryptionApiError, requestChallenge } from '@/utils/courses-encryption-api';

/** How many times a `stale-nonce` answer is retried with a fresh challenge. */
export const STALE_NONCE_RETRIES = 1;

/**
 * @callback Signer
 * @param {string} nonce the fresh challenge to sign over
 * @param {{ op?: string, params?: object }} context the operation the caller is
 *   performing and the request parameters its canonical `fields` derive from —
 *   e.g. `{ op: 'approve', params: { deviceId, wrappedMk } }`; the signer hashes
 *   what the contract says to hash (`wrappedMkHash`, `recoveryWrappingHash`).
 * @returns {Promise<string>} the base64 signature (P-256 ECDSA, raw r||s)
 */

/**
 * @template T
 * @param {{ token: string, deviceId?: string|null }} auth
 * @param {Signer} signer
 * @param {(signature: string, nonce: string) => Promise<T>} call the API call to
 *   make with the signature; it must throw the `EncryptionApiError` the client
 *   produced so a `stale-nonce` can be recognised
 * @param {{ op?: string, params?: object, challenge?: (auth) => Promise<string> }} [options]
 *   `op`/`params` reach the signer verbatim; `challenge` overrides the challenge
 *   request (tests and callers with their own client)
 * @returns {Promise<T>} whatever `call` resolves to
 * @throws the second `stale-nonce`, any `signature-rejected`, and every other
 *   error from the challenge, the signer or the call, unchanged and unlogged
 */
export async function withFreshChallenge(auth, signer, call, options = {}) {
  if (typeof signer !== 'function') {
    throw new TypeError('withFreshChallenge: signer(nonce, context) must be a function');
  }
  if (typeof call !== 'function') {
    throw new TypeError('withFreshChallenge: call(signature, nonce) must be a function');
  }
  const { challenge = requestChallenge, ...context } = options;

  for (let attempt = 0; ; attempt += 1) {
    const nonce = await challenge(auth);
    const signature = await signer(nonce, context);
    if (typeof signature !== 'string' || !signature) {
      throw new TypeError(
        'withFreshChallenge: signer(nonce) must resolve to a base64 signature string',
      );
    }
    try {
      return await call(signature, nonce);
    } catch (error) {
      if (attempt < STALE_NONCE_RETRIES && isEncryptionApiError(error, 'stale-nonce')) continue;
      throw error;
    }
  }
}
